import { authenticateUserValues } from '../constants';
import Cookies from 'universal-cookie';
import axios, { axiosV3 } from 'lib/axios';
import { handleRedirectLink } from 'commons/helper';
import { jwtDecode } from 'jwt-decode';

const { TOKEN, DOMAIN, SPARROW_APPS_TOKEN } = authenticateUserValues;
const cookies = new Cookies();

export const authenticateUser = () => {
  try {
    const paramString = document.URL.split('?')[1];
    let queryString = new URLSearchParams(paramString);
    let urlParams: any = {};
    for (let pair of queryString.entries()) {
      urlParams[String(pair[0])] = pair[1];
    }
    const hostDomain = `.${window.location.hostname
      ?.split('.')
      ?.slice(-2)
      ?.join('.')}`;
    const domain = urlParams[DOMAIN];
    const token = urlParams[SPARROW_APPS_TOKEN];
    const decodeToken: any = jwtDecode(token);
    const expirationDate = new Date(decodeToken.exp * 1000);
    cookies.set(TOKEN, token, {
      domain: hostDomain,
      expires: expirationDate
    });
    cookies.set(DOMAIN, domain);
  } catch (error) {
    handleRedirectLink();
  }
};

const getHeader = () => ({ headers: { auth: cookies.get(TOKEN) } });

// Update headers when token changes for axios (v2/api)
axios.interceptors.request.use((config) => {
  config.headers.auth = cookies.get(TOKEN);
  return config;
});

// Update headers when token changes for v3 API
axiosV3.interceptors.request.use((config) => {
  config.headers.auth = cookies.get(TOKEN);
  return config;
});

const getAllApps = async () => {
  const res = await axios.get('/get-all-developed-apps', getHeader());
  return res?.data;
};

const verifyDeleteApp = async (appId: number) => {
  const response = await axios.get(
    `/verify-delete?appId=${appId}`,
    getHeader()
  );
  return response?.data;
};

const deleteSingleApp = async (appId: number) => {
  await axios.delete(`/delete-app?appId=${appId}`, getHeader());
};

const updateSingleApp = async (formData: FormData) => {
  await axios.put('/update-marketplace-app', formData, getHeader());
};

const uploadSingleApp = async (formData: FormData) => {
  await axios.post('/upload-marketplace-app', formData, getHeader());
};

const uploadLocalDevApp = async (formData: FormData) => {
  const response = await axios.post(
    '/development/upload-marketplace-app',
    formData,
    getHeader()
  );
  return response?.data;
};

const updateLocalDevApp = async (formData: FormData) => {
  const response = await axios.put(
    '/development/update-marketplace-app',
    formData,
    getHeader()
  );
  return response?.data;
};

const deleteLocalDevApp = async (appId: number) => {
  await axios.delete(`/development/delete-app?appId=${appId}`, getHeader());
};

const getAppStorageUsage = async (
  appId: number,
  limit: number = 10,
  nextToken?: string | null
) => {
  const params = new URLSearchParams({
    appId: appId.toString(),
    limit: limit.toString()
  });
  if (nextToken) {
    params.append('nextToken', nextToken);
  }
  const response = await axios.get(
    `/usage/app-storage?${params.toString()}`,
    getHeader()
  );
  return response?.data;
};

// const getAppFilesUsage = async (appId: number) => {
//   const params = new URLSearchParams({
//     appId: appId.toString()
//   });
//   const response = await axios.get(`/usage/app-files?${params.toString()}`, getHeader());
//   return response?.data;
// };

const getAppScheduleUsage = async (appId: number) => {
  const params = new URLSearchParams({
    appId: appId.toString()
  });
  const response = await axios.get(
    `/usage/app-schedule?${params.toString()}`,
    getHeader()
  );
  return response?.data;
};

const getEventLogs = async (filters?: {
  startDate?: string;
  endDate?: string;
  appId?: string;
  appName?: string;
  eventId?: string;
  traceId?: string;
  pageNumber?: number;
  pageSize?: number;
}) => {
  const params = new URLSearchParams();
  if (filters?.startDate) params.append('startDate', filters.startDate);
  if (filters?.endDate) params.append('endDate', filters.endDate);
  if (filters?.appId) params.append('appId', filters.appId);
  if (filters?.appName) params.append('appName', filters.appName);
  if (filters?.eventId) params.append('eventId', filters.eventId);
  if (filters?.traceId) params.append('traceId', filters.traceId);
  if (filters?.pageNumber !== undefined)
    params.append('pageNumber', filters.pageNumber.toString());
  if (filters?.pageSize !== undefined)
    params.append('pageSize', filters.pageSize.toString());

  const response = await axios.get(
    `/audit/app-events?${params.toString()}`,
    getHeader()
  );
  return response?.data;
};

const getSingleApp = async (id: string) => {
  const response = await axios.get(
    `get-developed-app-detail?appId=${id}`,
    getHeader()
  );
  return response?.data;
};

const getAllWebhookEvents = async () => {
  const response = await axios.get('/get-all-events', getHeader());
  return response?.data;
};

const getAllProductEvents = async () => {
  const response = await axios.get('/get-all-product-events', getHeader());
  return response?.data;
};

const getAllLocations = async () => {
  const response = await axios.get('/get-all-locations', getHeader());
  return response?.data;
};

const authenticateMCP = async (productKey: string, appId?: number) => {
  const payload: any = { productKey };
  if (appId) {
    payload.appId = appId;
  }
  const response = await axios.post('/mcp/authenticate', payload, getHeader());
  return response?.data;
};

const getAllWebhookApps = async () => {
  const response = await axios.get('/get-all-webhooks', getHeader());
  return response?.data;
};

const getCurrentSecretToken = async (id: string | null) => {
  const response = await axios.get(
    `/get-secret-key${id ? `?webhookId=${id}` : ''}`,
    getHeader()
  );
  return response?.data;
};

const regenerateSecretToken = async () => {
  const response = await axios.get('/regenerate-secret-key', getHeader());
  return response?.data;
};

const uploadWebhookApp = async (formData: FormData) => {
  const response = await axios.post(
    '/upload-marketplace-app',
    formData,
    getHeader()
  );
  return response?.data;
};

const updateWebhookApp = async (formData: FormData) => {
  const response = await axios.put(
    '/update-marketplace-app',
    formData,
    getHeader()
  );
  return response?.data;
};

const validateURL = async (payload: {
  url: string;
  authType: string;
  headers: any;
}) => {
  const response = await axios.post(`/validate-url`, payload, getHeader());
  return response?.data;
};

const disableWebhook = async (id: number) => {
  const response = await axios.delete(
    `/disable-webhook?webhookId=${id}`,
    getHeader()
  );
  return response?.data;
};

const deleteWebhook = async (id: number) => {
  const response = await axios.delete(
    `/delete-webhook?webhookId=${id}`,
    getHeader()
  );
  return response?.data;
};

const getCurrentWebhook = async (id: number) => {
  const response = await axios.get(`/get-webhook?webhookId=${id}`, getHeader());
  return response?.data;
};

// New API endpoints for app creation flow

// Get signed URLs for uploading icon and zip files
// iconExtension is optional - pass it when uploading an icon (e.g., 'png', 'jpg')
const getUploadSignedUrls = async (iconExtension?: string) => {
  const url = iconExtension
    ? `/developer-portal/app-upload-request/signed-url?type=${iconExtension}`
    : `/developer-portal/app-upload-request/signed-url`;
  const response = await axios.get(url);
  return response?.data;
};

// Create a new app (POST)
const createApp = async (payload: any) => {
  const response = await axios.post('/developer-portal/create-app', payload);
  return response?.data;
};

// Update an existing app (PUT)
const updateApp = async (payload: any) => {
  const response = await axios.put('/developer-portal/update-app', payload);
  return response?.data;
};

// Delete app release request
const deleteReleaseRequest = async (
  releaseRequestId: number,
  note: string = 'Update New App Zip'
) => {
  const response = await axios.delete(
    `/developer-portal/app-release-request/${releaseRequestId}`,
    {
      data: {
        note
      }
    }
  );
  return response?.data;
};

// Add comment to app release request
const addAppReleaseRequestComment = async (payload: {
  releaseRequestId: number;
  comment: string;
}) => {
  const response = await axios.post(
    `/developer-portal/app-release-request/comment/${payload.releaseRequestId}`,
    { comment: payload.comment }
  );
  return response?.data;
};

const addAppReleaseRequestCommentById = async (payload: {
  releaseRequestId: number;
  comment: string;
}) => {
  const response = await axios.post(
    `/app-release-request/comment/${payload.releaseRequestId}`,
    { comment: payload.comment },
    getHeader()
  );
  return response?.data;
};

const getAppReleaseRequests = async (appId: number | string) => {
  const response = await axios.get(
    `/app-release-request/${appId}`,
    getHeader()
  );
  return response?.data;
};

// Helper function to upload file to S3 using presigned URL
export const uploadFileToS3 = async (
  file: File,
  presignedUrl: string
): Promise<string> => {
  // Extract the S3 URL from the presigned URL (before the query parameters)
  let s3Url = presignedUrl.split('?')[0];

  // Decode the URL to handle double-encoded characters (like %252F which is double-encoded /)
  // We need to decode multiple times in case of double encoding
  try {
    let decoded = decodeURIComponent(s3Url);
    // Decode again if there are still encoded characters (handles double encoding)
    if (decoded.includes('%')) {
      decoded = decodeURIComponent(decoded);
    }
    s3Url = decoded;
  } catch (e) {
    // If decoding fails, use original URL
    console.warn('Failed to decode S3 URL, using original:', e);
  }

  // Normalize the URL to remove double slashes (except after http:// or https://)
  // Replace multiple consecutive slashes with a single slash, but preserve the protocol
  s3Url = s3Url.replace(/([^:]\/)\/+/g, '$1');

  // Remove trailing slash if present (S3 object URLs shouldn't have trailing slashes)
  s3Url = s3Url.replace(/\/$/, '');

  try {
    // Upload file to S3 using PUT request with fetch
    // IMPORTANT: Do NOT set any headers
    // The presigned URL signature is calculated without Content-Type
    // Setting Content-Type or other headers will cause signature mismatch
    // Also, setting headers can trigger CORS preflight
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      mode: 'cors'
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to upload file to S3: ${response.status} ${response.statusText} - ${errorText}`
      );
    }

    // Return the normalized S3 URL (without query parameters and double slashes)
    return s3Url;
  } catch (error: any) {
    // Provide helpful error message
    if (
      error.message?.includes('CORS') ||
      error.message?.includes('fetch') ||
      error.message?.includes('Failed to fetch')
    ) {
      throw new Error(
        `CORS error: Unable to upload to S3. The S3 bucket CORS policy needs to allow PUT requests from your origin. ` +
          `Please configure the S3 bucket CORS policy to allow PUT requests from your domain. Original error: ${
            error?.message ?? String(error)
          }`
      );
    }
    throw error;
  }
};

export const devPortalServices = {
  getAllApps,
  getSingleApp,
  uploadSingleApp,
  updateSingleApp,
  deleteSingleApp,
  verifyDeleteApp,
  getAllWebhookEvents,
  getAllProductEvents,
  getAllWebhookApps,
  getCurrentSecretToken,
  regenerateSecretToken,
  uploadWebhookApp,
  updateWebhookApp,
  validateURL,
  disableWebhook,
  getCurrentWebhook,
  deleteWebhook,
  getAllLocations,
  authenticateMCP,
  uploadLocalDevApp,
  updateLocalDevApp,
  deleteLocalDevApp,
  getUploadSignedUrls,
  createApp,
  updateApp,
  deleteReleaseRequest,
  addAppReleaseRequestComment,
  addAppReleaseRequestCommentById,
  getAppReleaseRequests,
  getAppStorageUsage,
  getAppScheduleUsage,
  getEventLogs
  // getAppFilesUsage - TODO: Uncomment when API is ready
};

//Survey Sparrow Embedded Chat Survey
export const sparrowLaunch = (opts: any) => {
  let e = 'ss-widget',
    t = 'script',
    a = document,
    r = window,
    l = localStorage;
  let s,
    n: any,
    c,
    rm = a.getElementById('SS_SCRIPT');
  r.SS_WIDGET_TOKEN = 'tt-gJSiknKce8Fkur86y7VpKt';
  r.SS_ACCOUNT = 'feedback.surveysparrow.com';
  r.SS_SURVEY_NAME = 'Developer-Portal-Chat';
  if (
    !a.getElementById(e) &&
    !l.getItem('removed-ss-widget-tt-gJSiknKce8Fkur86y7VpKt')
  ) {
    let S: any = function () {
      S.update(arguments);
    };
    S.args = [];
    S.update = function (e: any) {
      this.S.args.push(e);
    };
    r.SparrowLauncher = S;
    s = a.getElementsByTagName(t);
    c = s[s.length - 1];
    n = a.createElement(t);
    n.type = 'text/javascript';
    n.async = !0;
    n.id = e;
    n.src = [
      'https://',
      'feedback.surveysparrow.com/widget/',
      r.SS_WIDGET_TOKEN,
      '?',
      'customParams=',
      JSON.stringify(opts)
    ].join('');
    c?.parentNode?.insertBefore(n, c);
    r.SS_VARIABLES = opts;
    rm?.parentNode?.removeChild(r as any);
  }
};
